import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  IconButton,
  Chip,
  Alert,
  Breadcrumbs,
  Link,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  NavigateNext as NavigateNextIcon,
  Lock as LockIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  AccountBalanceWallet as WalletIcon,
  Group as GroupIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { getCommissionBySponsor } from '../../services/associate';
import { getProfileDetail } from '../../services/profile';
import RefreshButton from '../../components/RefreshButton/RefreshButton';

const MyBusiness = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [totalCommissionEarned, setTotalCommissionEarned] = useState(0);
  const [totalBusinessAmount, setTotalBusinessAmount] = useState(0);
  const [selfBusinessAmount, setSelfBusinessAmount] = useState(0);
  const [teamBusinessAmount, setTeamBusinessAmount] = useState(0);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const formatDateOnly = (v) => {
    if (!v) return 'N/A';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // 1. Get current user profile to get their ID
      const profile = await getProfileDetail();
      const userData = profile?.data || profile;
      setProfileData(userData);

      if (userData?._id) {
        // 2. Fetch commissions using the user's ID
        const result = await getCommissionBySponsor(userData._id, {
          page: page + 1,
          limit: rowsPerPage
        });
        if (result.success) {
          setHistoryData(result.data || []);
          setTotalCommissionEarned(result.totalCommissionEarned || 0);
          setTotalBusinessAmount(result.totalBusinessAmount || 0);
          setSelfBusinessAmount(result.selfBusinessAmount || 0);
          setTeamBusinessAmount(result.teamBusinessAmount || 0);
          setTotalItems(result.pagination?.totalItems || result.totalRecords || 0);
        } else {
          setError('Failed to load business data');
        }
      } else {
        setError('User identification failed');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while fetching business data');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Data is now handled server-side
  const paginatedHistoryData = historyData;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Breadcrumbs */}
      {/* <Box sx={{ mb: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              history.push('/app/dashboard');
            }}
          >
            Dashboard
          </Link>
          <Typography color="text.primary">My Business</Typography>
        </Breadcrumbs>
      </Box> */}

      <Box sx={{
        mb: 4,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
          <IconButton onClick={() => history.goBack()} color="primary" sx={{ bgcolor: 'white', boxShadow: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon fontSize="large" sx={{ color: '#f57c00' }} />
              My Business
            </Typography>
          </Box>
        </Box>
        <RefreshButton onClick={fetchData} loading={loading} />
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Business', amount: totalBusinessAmount, icon: <WalletIcon fontSize="large" />, color: '#1976d2', bgColor: alpha('#1976d2', 0.1) },
          { label: 'Self Business', amount: selfBusinessAmount, icon: <PersonIcon fontSize="large" />, color: '#9c27b0', bgColor: alpha('#9c27b0', 0.1) },
          { label: 'Team Business', amount: teamBusinessAmount, icon: <GroupIcon fontSize="large" />, color: '#00897b', bgColor: alpha('#00897b', 0.1) },
          { label: 'Total Commission', amount: totalCommissionEarned, icon: <CurrencyRupeeIcon fontSize="large" />, color: '#f57c00', bgColor: alpha('#f57c00', 0.1) },
        ].map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                bgcolor: 'white',
                borderRadius: 4,
                boxShadow: `0 4px 12px ${alpha(stat.color, 0.08)}`,
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 1.2 }}>
                      {stat.label}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: stat.color, mt: 1 }}>
                      ₹{(stat.amount || 0).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: stat.bgColor, color: stat.color, width: 44, height: 44 }}>
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : error ? (
        <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>{error}</Alert>
      ) : (
        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden' }}>
          <CardContent sx={{ p: 0 }}>
            {historyData.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography variant="h6" color="text.secondary">No business transactions found yet.</Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={() => history.push('/app/dashboard')}>Go to Dashboard</Button>
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 800 }}>
                  <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Booking No</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Receipt No</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Associate</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Receipt Amount</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Distribution Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Commission (%)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Commission Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Remark</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedHistoryData.map((row, index) => (
                      <TableRow key={row._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, bgcolor: index % 2 === 0 ? 'inherit' : '#fafafa' }}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateOnly(row.receivedDate)}</TableCell>
                        <TableCell>
                          <Chip
                            label="Approve"
                            size="small"
                            color="success"
                            sx={{ fontWeight: 700, minWidth: 65 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.bookingId ? (
                            <Typography
                              component="span"
                              onClick={() => history.push(`/app/booking/BookingDetails/${row.bookingId}`)}
                              sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontWeight: 600,
                                '&:hover': { color: 'primary.dark' }
                              }}
                            >
                              {row.bookingNumber || row.bookingId}
                            </Typography>
                          ) : (
                            row.bookingNumber || '-'
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.receiptNumber ? (
                            <Typography
                              component="span"
                              sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontWeight: 600,
                                '&:hover': { color: 'primary.dark' }
                              }}
                            >
                              {row.receiptNumber}
                            </Typography>
                          ) : (
                            row.receiptNumber || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {row.associateName || 'Self'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">₹{(row.receiptAmount || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">₹{(row.paymentAmount || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell>{row.commissionPercent ? `${row.commissionPercent}%` : '-'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900, color: 'primary.main', fontSize: '1.05rem' }}>
                          ₹{(row.commissionAmount || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.receiptNumber ? `Receipt #${row.receiptNumber}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {totalItems > 0 && (
              <TablePagination

                component="div"
                count={totalItems}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MyBusiness;